import React, { FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IRequestAdditionalReferences } from '../../../../common/models/referenceRequest/IRequestAdditionalReferences';
import { requestAdditionalReferencesRoutine } from '../../../RequestBuilder/routines';
import { IContact } from '../../../../common/models/contact/IContact';
import IconButton from '../../../../components/IconButton';
import Icon from '../../../../components/Icon';
import CustomPopover from '../../../../components/CustomPopover';
import { CandidateStatus } from '../../../../common/enums/CandidateStatus';
import { IProfileCandidate } from '../../../../common/models/candidate/IProfileCandidate';
import {
  CandidateActionType,
  getActiveActions,
  getInactiveActions,
  mapContactToIReqAdditionalReferences
} from './utils';
import {
  updateCandidateOwnerRoutine,
  updateCandidateStatusRoutine
} from '../../../../containers/CandidatesBulkActions/routines';
import { ICandidateUpdateStatus } from '../../../../common/models/candidate/ICandidateUpdateStatus';
import ChangePositionModal from '../ChangePositionModal';
import ArchiveCandidateModal from '../../../../containers/ArchiveCandidateModal';
import SendReminderModal from '../../../../containers/SendReminderModal';
import ChangeOwner from '../../../../containers/ChangeOwner';
import styles from './styles.module.scss';
import EditContactModal from '../EditContactModal';
import { IUpdateOwner } from '../../../../common/models/candidate/IUpdateOwner';

interface IProps {
  shownAction: CandidateActionType | null;
  candidateId: string;
  contact: IContact;
  candidateData: IProfileCandidate;
  setShownAction: IBindingCallback1<CandidateActionType | null>;
  updateStatus: IBindingCallback1<ICandidateUpdateStatus>;
  requestAdditionalReferences: IBindingCallback1<IRequestAdditionalReferences>;
  updateCandidateOwner: IBindingCallback1<IUpdateOwner>;
}

const CandidateActionsPopup: FunctionComponent<IProps> = ({
  contact,
  candidateId,
  candidateData,
  shownAction,
  setShownAction,
  updateStatus,
  updateCandidateOwner,
  requestAdditionalReferences
}) => {
  const onReqAdditional = () => requestAdditionalReferences(
    mapContactToIReqAdditionalReferences(contact, candidateData, candidateId)
  );

  const onHide = () => setShownAction(null);

  const onUpdateStatus = () => updateStatus({ ids: [candidateId], status: CandidateStatus.Active });

  const actions = candidateData.status === CandidateStatus.Active
    ? getActiveActions(onReqAdditional)
    : getInactiveActions(onUpdateStatus);

  const Body = () => (
    <div className={styles.popupBody}>
      {actions.map(({ id, defaultMessage, type, icon, action }) => (
        <div
          role="presentation"
          onClick={action || (() => setShownAction(type))}
          className={styles.actionItem}
          key={type}
        >
          <Icon className={styles.actionIcon} icon={icon} />
          <span className={styles.actionLabel}><FormattedMessage id={id} defaultMessage={defaultMessage} /></span>
        </div>
      ))}
    </div>
  );

  const Trigger = () => <IconButton variant="bordered" icon={<Icon icon="more" />} />;

  return (
    <div className={styles.actionsPopupContainer}>
      <ChangePositionModal isShown={shownAction === 'position'} onHide={onHide} />
      <ArchiveCandidateModal isShown={shownAction === 'archive'} onHide={onHide} candidates={[candidateId]} />
      <SendReminderModal
        isShown={shownAction === 'reminder'}
        onHide={onHide}
        candidates={[{ id: candidateId, email: contact.email, phoneNumber: contact.phoneNumber }]}
      />
      <ChangeOwner
        isShown={shownAction === 'reassign'}
        onHide={onHide}
        variant="candidates"
        ids={[candidateId]}
        updateOwner={updateCandidateOwner}
      />
      <EditContactModal
        openReminder={() => setShownAction('reminder')}
        isShown={shownAction === 'edit'}
        onHide={onHide}
      />
      <CustomPopover
        key="reference-counters"
        placement="bottom"
        trigger={<Trigger />}
        showArrow
        rootClose
        popoverClassName={styles.actionsPopover}
      >
        <Body />
      </CustomPopover>
    </div>
  );
};

const mapStateToProps = ({
  contactProfile: { contact: { candidateId, contact }, references: { candidateData } }
}: IAppState) => ({ candidateId, contact, candidateData: candidateData as IProfileCandidate });

const mapDispachToProps = {
  requestAdditionalReferences: requestAdditionalReferencesRoutine,
  updateCandidateOwner: updateCandidateOwnerRoutine,
  updateStatus: updateCandidateStatusRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(CandidateActionsPopup);
